/**
 * Utility for extracting structured diagnostic information from AWS SDK errors.
 * Used to enrich upload error logging with actionable context.
 * #phase:P4
 */

export interface AwsErrorDetails {
  errorName?: string;
  errorCode?: string;
  httpStatus?: number;
  requestId?: string;
  retryCount?: number;
  message: string;
  // Serialized for logging (avoid circular refs)
  rawError?: string;
}

interface ErrorLike {
  message?: unknown;
  name?: unknown;
  Code?: unknown;
  code?: unknown;
  $response?: {
    statusCode?: unknown;
  };
  $metadata?: {
    requestId?: unknown;
    attempts?: unknown;
  };
}

/**
 * Extract structured error details from AWS SDK error objects.
 * Handles various error shapes from @aws-sdk/client-s3 and @aws-sdk/lib-storage.
 */
export function extractAwsErrorDetails(err: unknown): AwsErrorDetails {
  const fallback: AwsErrorDetails = {
    message: String(err),
  };

  if (!err || typeof err !== 'object') {
    return fallback;
  }

  const error: ErrorLike = err;

  const details: AwsErrorDetails = {
    message: error.message ? String(error.message) : String(err),
  };

  // AWS SDK error name (e.g., NetworkingError, TimeoutError, ServiceException)
  if (typeof error.name === 'string') {
    details.errorName = error.name;
  }

  // AWS error code (e.g., AccessDenied, ExpiredToken)
  if (typeof error.Code === 'string') {
    details.errorCode = error.Code;
  } else if (typeof error.code === 'string') {
    details.errorCode = error.code;
  }

  // HTTP status from $response
  if (error.$response && typeof error.$response.statusCode === 'number') {
    details.httpStatus = error.$response.statusCode;
  }

  // Request ID for AWS support
  if (error.$metadata) {
    if (typeof error.$metadata.requestId === 'string') {
      details.requestId = error.$metadata.requestId;
    }
    if (typeof error.$metadata.attempts === 'number') {
      details.retryCount = error.$metadata.attempts;
    }
  }

  // Serialize error for deep inspection (limit size to prevent log bloat)
  try {
    const serialized = JSON.stringify(
      err,
      (_, value) => {
        // Skip large binary data and circular refs
        if (value instanceof Blob || value instanceof File) return '[Blob]';
        if (value instanceof ArrayBuffer) return '[ArrayBuffer]';
        return value;
      },
      2,
    );
    details.rawError =
      serialized.length > 2000
        ? serialized.slice(0, 2000) + '...[truncated]'
        : serialized;
  } catch {
    // JSON serialization failed (likely circular refs)
    details.rawError = '[serialization failed]';
  }

  return details;
}
